import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../components/ScrollReveal';
import SEO from '../components/SEO';

export default function Forside() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [expandedServices, setExpandedServices] = useState(false);
  const carouselRef = useRef(null);

  const scroll = (direction) => {
    if (carouselRef.current) {
      const { scrollLeft, clientWidth } = carouselRef.current;
      // Calculate scroll step based on responsive visible cards
      const cardWidth = clientWidth / (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1);
      const scrollTo = direction === 'left' ? scrollLeft - cardWidth : scrollLeft + cardWidth;
      carouselRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [heroFormData, setHeroFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  });
  const [heroFormStatus, setHeroFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  useEffect(() => {
    // Fetch services
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setServices(data);
        }
      })
      .catch((err) => console.error('Error fetching services:', err));

    // Fetch reviews
    fetch('https://api.dev.auxo.dk/public/content/referencer')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setReviews(data);
        }
      })
      .catch((err) => console.error('Error fetching reviews:', err));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeroInputChange = (e) => {
    const { name, value } = e.target;
    setHeroFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault();
    setHeroFormStatus({ submitting: true, success: false, error: null });

    if (!heroFormData.name || !heroFormData.phone || !heroFormData.email || !heroFormData.description) {
      setHeroFormStatus({
        submitting: false,
        success: false,
        error: 'Venligst udfyld alle påkrævede felter.',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(heroFormData),
      });

      if (response.ok) {
        setHeroFormStatus({ submitting: false, success: true, error: null });
        setHeroFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        const errData = await response.json();
        setHeroFormStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der skete en fejl ved afsendelse. Prøv venligst igen.',
        });
      }
    } catch (err) {
      console.error('Hero form submission error:', err);
      setHeroFormStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.',
      });
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: null });

    // Client-side validation
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Venligst udfyld alle påkrævede felter.',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        const errData = await response.json();
        setFormStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der skete en fejl ved afsendelse. Prøv venligst igen.',
        });
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.',
      });
    }
  };

  // Focus boxes data
  const focusBoxes = [
    {
      title: 'Kloak',
      desc: 'Autoriseret kloakmesterarbejde, nyanlæg, reparation og rådgivning.',
      icon: 'fa-solid fa-pipe-valve',
      link: '/ydelser/kloakmester',
    },
    {
      title: 'Kloakspuling',
      desc: 'Hurtig og effektiv spuling af stoppede kloakker, rør og brønde.',
      icon: 'fa-solid fa-truck-droplet',
      link: '/ydelser/kloakspuling',
    },
    {
      title: 'Støbearbejde',
      desc: 'Præcis støbning af fundamenter, gulve og sokler til alle formål.',
      icon: 'fa-solid fa-trowel-bricks',
      link: '/ydelser/stoebearbejde',
    },
    {
      title: 'Belægning',
      desc: 'Flotte og holdbare flisebelægninger til indkørsler, terrasser og stier.',
      icon: 'fa-solid fa-cubes',
      link: '/ydelser/belaegning',
    },
  ];

  return (
    <div className="bg-brand-navy-50">
      <SEO
        title="Autoriseret Kloakmester & Entreprenør | Jan Nørgaard"
        description="Autoriseret kloakmester og entreprenør Jan Nørgaard ApS med over 20 års erfaring. Kloakspuling, dræn, belægning og støbearbejde. Få et tilbud."
        image="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/kloakspuling-og-tv-inspektion.jpg"
      />
      {/* 1. HERO SECTION */}
      <section className="relative bg-brand-navy-900 text-white py-24 md:py-32 lg:py-40 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/gravemaskine-nedbrydning-jordarbejde.jpg"
            alt="Gravemaskine udfører nedbrydnings- og jordarbejde på byggeplads"
            title="Entreprenørarbejde og nedbrydning med gravemaskine"
            className="h-full w-full object-cover object-center opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy-950/90 via-brand-navy-950/50 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Left side: Text content */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange-500/20 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange-400 border border-brand-orange-500/30 mb-4 sm:mb-6">
                <i className="fa-solid fa-award text-brand-orange-400" />
                <span>Autoriseret faglighed &amp; erfaring</span>
              </div>
              <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl lg:text-[50px] leading-[1.15]">
                Autoriseret kloakmester &amp; entreprenør
                <span className="block mt-1 sm:mt-2 text-brand-orange-400 font-bold text-2xl sm:text-3xl md:text-4xl lg:text-[40px] leading-tight">
                  med mere end 20 års erfaring
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg md:text-xl text-brand-navy-200 leading-relaxed max-w-2xl font-normal">
                Hos Entreprenør Jan Nørgaard får du hjælp af en autoriseret kloakmester og erfaren entreprenør, når opgaven kræver faglighed, godt overblik og en løsning, der passer til stedet. Vi hjælper private og erhverv med kloakarbejde som <Link to="/ydelser/kloakspuling" className="text-white font-medium underline decoration-brand-orange-400 decoration-2 underline-offset-4 hover:text-brand-orange-400 transition-colors">kloakspuling</Link>, jordarbejde, <Link to="/ydelser/draen-etablering-reparation" className="text-white font-medium underline decoration-brand-orange-400 decoration-2 underline-offset-4 hover:text-brand-orange-400 transition-colors">dræn</Link>, <Link to="/ydelser/belaegning" className="text-white font-medium underline decoration-brand-orange-400 decoration-2 underline-offset-4 hover:text-brand-orange-400 transition-colors">belægning</Link> og beton.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded bg-brand-orange-500 px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600 shadow-lg shadow-brand-orange-500/20"
                >
                  Kontakt os <i className="fa-regular fa-arrow-right ml-2" />
                </Link>
                <a
                  href="tel:20483818"
                  className="inline-flex items-center justify-center rounded border border-white/20 bg-white/10 px-5 py-3 font-display text-sm font-bold uppercase tracking-wider text-white backdrop-blur-sm transition-colors hover:bg-white hover:text-brand-navy-900"
                >
                  <i className="fa-solid fa-phone mr-2 text-brand-orange-400" /> 20 48 38 18
                </a>
              </div>

              {/* Quick trust metrics */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 pt-6 border-t border-white/15">
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-brand-orange-400 text-sm shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-navy-100">Autoriseret garanti</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-brand-orange-400 text-sm shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-navy-100">Over 20 års erfaring</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-brand-orange-400 text-sm shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-navy-100">Private &amp; erhverv</span>
                </div>
              </div>
            </div>

            {/* Right side: Webform */}
            <div className="lg:col-span-5">
              <ScrollReveal direction="right" className="rounded-lg bg-white p-6 sm:p-8 shadow-2xl border border-brand-navy-800/10 text-brand-navy-900">
                <h3 className="font-display text-xl font-bold uppercase text-brand-navy-900 mb-2">
                  Få et gratis tilbud
                </h3>
                <p className="text-xs text-brand-navy-500 mb-6">
                  Udfyld formularen, så vender vi tilbage med et uforpligtende tilbud inden for 24 timer.
                </p>

                <form onSubmit={handleHeroFormSubmit} className="space-y-4">
                  {/* Honeypot Field - Absolutely Hidden */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="hero_website_url">Hjemmeside URL</label>
                    <input
                      type="text"
                      id="hero_website_url"
                      name="website_url"
                      value={heroFormData.website_url}
                      onChange={handleHeroInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div>
                    <label htmlFor="hero_name" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                      Navn <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="hero_name"
                      name="name"
                      required
                      value={heroFormData.name}
                      onChange={handleHeroInputChange}
                      className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="hero_phone" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="hero_phone"
                        name="phone"
                        required
                        value={heroFormData.phone}
                        onChange={handleHeroInputChange}
                        className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                        placeholder="Telefonnummer"
                      />
                    </div>
                    <div>
                      <label htmlFor="hero_email" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="hero_email"
                        name="email"
                        required
                        value={heroFormData.email}
                        onChange={handleHeroInputChange}
                        className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                        placeholder="E-mail adresse"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="hero_description" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                      Beskrivelse af opgaven <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="hero_description"
                      name="description"
                      required
                      rows="3"
                      value={heroFormData.description}
                      onChange={handleHeroInputChange}
                      className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                      placeholder="Beskriv dit projekt eller problem..."
                    />
                  </div>

                  {heroFormStatus.error && (
                    <div className="rounded bg-red-50 p-3 text-sm font-semibold text-red-600 border border-red-200">
                      <i className="fa-solid fa-circle-exclamation mr-1.5" /> {heroFormStatus.error}
                    </div>
                  )}

                  {heroFormStatus.success && (
                    <div className="rounded bg-green-50 p-3 text-sm font-semibold text-green-600 border border-green-200">
                      <i className="fa-solid fa-circle-check mr-1.5" /> Tak for din henvendelse! Vi vender tilbage hurtigst muligt.
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={heroFormStatus.submitting}
                    className="w-full rounded bg-brand-orange-500 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600 disabled:opacity-50 cursor-pointer"
                  >
                    {heroFormStatus.submitting ? (
                      <span>Sender... <i className="fa-solid fa-spinner animate-spin ml-1" /></span>
                    ) : (
                      <span>Få et gratis tilbud <i className="fa-regular fa-paper-plane ml-1" /></span>
                    )}
                  </button>
                </form>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* 2. FOCUS BOXES (Fokuskasser) */}
      <section className="relative z-20 -mt-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {focusBoxes.map((box, idx) => (
            <ScrollReveal
              key={idx}
              delay={idx * 100}
              direction="up"
              className="flex flex-col"
            >
              <Link
                to={box.link}
                className="flex flex-col h-full rounded-lg border-t-4 border-t-brand-orange-500 border-x border-b border-brand-navy-200 bg-white p-6 shadow-lg transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-brand-orange-500/30 group cursor-pointer"
              >
                <div className="text-brand-navy-900 text-3xl mb-4 transition-colors duration-300 group-hover:text-brand-orange-500">
                  <i className={`${box.icon}`} />
                </div>
                <h3 className="mt-2 font-display text-lg font-bold uppercase text-brand-navy-900 transition-colors duration-300 group-hover:text-brand-orange-500 flex items-center justify-between">
                  <span>{box.title}</span>
                  <i className="fa-solid fa-arrow-right text-sm opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 text-brand-orange-500" />
                </h3>
                <p className="mt-2 flex-grow text-sm text-brand-navy-600 leading-relaxed">
                  {box.desc}
                </p>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* 3. SEO & EXPERTISE SECTION */}
      <section className="py-20 bg-white border-y border-brand-navy-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Main Title & Lead Introduction */}
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            <ScrollReveal direction="left" className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-orange-500/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-brand-orange-600 border border-brand-orange-500/20 mb-4">
                <i className="fa-solid fa-helmet-safety text-brand-orange-500" />
                <span>Kvalitet &amp; Pålidelighed</span>
              </div>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-brand-navy-900 sm:text-4xl md:text-5xl leading-tight">
                Solidt håndværk der holder i generationer
              </h2>
              <p className="mt-6 text-lg text-brand-navy-700 leading-relaxed font-medium">
                Professionelt entreprenør- og autoriseret kloakmesterarbejde i Sommersted, Jels, Vojens, Haderslev, Vejen og Kolding. Vi løser opgaver for både private og erhverv med fokus på høj faglighed, præcision og holdbare resultater.
              </p>

              {/* Quick trust metrics */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-brand-navy-100">
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-brand-orange-500 text-base shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-navy-900">Lokal forankring</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-brand-orange-500 text-base shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-navy-900">Moderne maskinpark</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <i className="fa-solid fa-circle-check text-brand-orange-500 text-base shrink-0" />
                  <span className="text-xs font-bold uppercase tracking-wide text-brand-navy-900">Uforpligtende tilbud</span>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="right" className="lg:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-brand-navy-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/kloakspuling-og-tv-inspektion.jpg"
                  alt="Specialudstyr og spuleslange til autoriseret kloakspuling og TV-inspektion"
                  title="Professionelt kloakspuling og TV-inspektion udstyr"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy-950/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-brand-navy-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-brand-orange-500 text-white flex items-center justify-center font-bold text-lg shadow-sm">
                      <i className="fa-solid fa-shield-check" />
                    </div>
                    <div>
                      <div className="font-display font-bold uppercase text-brand-navy-900 text-sm">Garanteret kvalitet</div>
                      <div className="text-xs text-brand-navy-600">Overholder alle lov- og miljøkrav</div>
                    </div>
                  </div>
                  <span className="font-display font-bold text-xl text-brand-orange-600">20+ år</span>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* 4 Thematic Content Cards */}
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Card 1: Hvorfor vælge én kloakmester til hele opgaven? */}
            <ScrollReveal direction="up" delay={50} className="flex flex-col">
              <div className="flex flex-col h-full rounded-xl bg-brand-navy-50/70 border border-brand-navy-200/80 p-7 lg:p-8 hover:border-brand-orange-500/40 hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-white border border-brand-navy-200 shadow-sm flex items-center justify-center text-brand-orange-500 text-xl group-hover:bg-brand-orange-500 group-hover:text-white group-hover:border-brand-orange-500 transition-colors">
                    <i className="fa-solid fa-pipe-valve" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-navy-500 bg-white px-3 py-1 rounded-full border border-brand-navy-200">
                    Helhedsløsning
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-brand-navy-900 leading-snug group-hover:text-brand-orange-600 transition-colors">
                  Hvorfor vælge én kloakmester til hele opgaven?
                </h3>
                <div className="mt-4 space-y-3.5 text-sm sm:text-base text-brand-navy-600 leading-relaxed flex-grow">
                  <p>
                    Som autoriseret kloakmester udfører vi både nye kloakanlæg, reparationer og løbende kloakservice. Vi hjælper også med separatkloakering, rottespærre, spildevandsanlæg og minirenseanlæg, så du kan samle rådgivning, gravearbejde og udførelse hos samme virksomhed.
                  </p>
                  <p>
                    Er problemet skjult under jorden, kan en TV inspektion give et præcist billede af rørenes tilstand og vise, hvor fejlen ligger. Det gør det lettere at vælge den rette løsning, hvad enten der er tale om et brud, en tilstopning eller behov for en egentlig reparation.
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-brand-navy-200/80 flex flex-wrap gap-2">
                  <Link to="/ydelser/kloakmester" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Kloakmester &rarr;
                  </Link>
                  <Link to="/ydelser/tv-inspektion" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    TV inspektion &rarr;
                  </Link>
                  <Link to="/ydelser/kloakseparering" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Separatkloakering &rarr;
                  </Link>
                  <Link to="/ydelser/rottespaerre" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Rottespærre &rarr;
                  </Link>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 2: Når kloakken stopper, kan kloakspuling genskabe gennemløbet */}
            <ScrollReveal direction="up" delay={100} className="flex flex-col">
              <div className="flex flex-col h-full rounded-xl bg-brand-navy-50/70 border border-brand-navy-200/80 p-7 lg:p-8 hover:border-brand-orange-500/40 hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-white border border-brand-navy-200 shadow-sm flex items-center justify-center text-brand-orange-500 text-xl group-hover:bg-brand-orange-500 group-hover:text-white group-hover:border-brand-orange-500 transition-colors">
                    <i className="fa-solid fa-truck-droplet" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-navy-500 bg-white px-3 py-1 rounded-full border border-brand-navy-200">
                    Spuling & Rensning
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-brand-navy-900 leading-snug group-hover:text-brand-orange-600 transition-colors">
                  Når kloakken stopper, kan kloakspuling genskabe gennemløbet
                </h3>
                <div className="mt-4 space-y-3.5 text-sm sm:text-base text-brand-navy-600 leading-relaxed flex-grow">
                  <p>
                    Ved en stoppet kloak er det vigtigt at finde årsagen frem for kun at behandle symptomet. Med professionel kloakspuling kan vi udføre rensning af kloak og fjerne slam og aflejringer, så vandet igen kan passere gennem rørene.
                  </p>
                  <p>
                    Vi udfører også slamsugning samt spuling af kloak og dræn. Hvis problemet vender tilbage, kan kloakspuling kombineres med TV inspektion, så vi kan vurdere, om der sidder rødder i ledningen, eller om et beskadiget rør kræver nærmere undersøgelse.
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-brand-navy-200/80 flex flex-wrap gap-2">
                  <Link to="/ydelser/kloakspuling" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Kloakspuling &rarr;
                  </Link>
                  <Link to="/ydelser/tv-inspektion" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    TV inspektion &rarr;
                  </Link>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-500 border border-brand-navy-200">
                    Slamsugning
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-500 border border-brand-navy-200">
                    Drænspuling
                  </span>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 3: Dræn og regnvand, få vandet ledt den rigtige vej */}
            <ScrollReveal direction="up" delay={150} className="flex flex-col">
              <div className="flex flex-col h-full rounded-xl bg-brand-navy-50/70 border border-brand-navy-200/80 p-7 lg:p-8 hover:border-brand-orange-500/40 hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-white border border-brand-navy-200 shadow-sm flex items-center justify-center text-brand-orange-500 text-xl group-hover:bg-brand-orange-500 group-hover:text-white group-hover:border-brand-orange-500 transition-colors">
                    <i className="fa-solid fa-cloud-showers-heavy" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-navy-500 bg-white px-3 py-1 rounded-full border border-brand-navy-200">
                    Klimatilpasning & Dræn
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-brand-navy-900 leading-snug group-hover:text-brand-orange-600 transition-colors">
                  Dræn og regnvand, få vandet ledt den rigtige vej
                </h3>
                <div className="mt-4 space-y-3.5 text-sm sm:text-base text-brand-navy-600 leading-relaxed flex-grow">
                  <p>
                    Et velfungerende dræn hjælper med at lede overskydende vand væk fra bygninger og arealer. Vi udfører nedgravning og opbygning af dræn samt dræn omkring huset, når vand eller fugt skal håndteres bedre omkring fundament og terræn.
                  </p>
                  <p>
                    Arbejdet kan omfatte udgravning, jordarbejde, drænspuling samt spuling af tilledninger med kloakspuling og løsninger til regnvand. Vi ser på terræn, vandets vej og den ønskede anvendelse af arealet, før vi planlægger arbejdet, så løsningens enkelte dele hænger naturligt sammen.
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-brand-navy-200/80 flex flex-wrap gap-2">
                  <Link to="/ydelser/draen-etablering-reparation" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Etablering af dræn &rarr;
                  </Link>
                  <Link to="/ydelser/omfangsdraen" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Omfangsdræn &rarr;
                  </Link>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-500 border border-brand-navy-200">
                    Jordarbejde
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-500 border border-brand-navy-200">
                    Regnvandsløsninger
                  </span>
                </div>
              </div>
            </ScrollReveal>

            {/* Card 4: Støbearbejde og fundamenter, et solidt udgangspunkt */}
            <ScrollReveal direction="up" delay={200} className="flex flex-col">
              <div className="flex flex-col h-full rounded-xl bg-brand-navy-50/70 border border-brand-navy-200/80 p-7 lg:p-8 hover:border-brand-orange-500/40 hover:shadow-lg transition-all duration-300 group">
                <div className="flex items-center justify-between gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-white border border-brand-navy-200 shadow-sm flex items-center justify-center text-brand-orange-500 text-xl group-hover:bg-brand-orange-500 group-hover:text-white group-hover:border-brand-orange-500 transition-colors">
                    <i className="fa-solid fa-trowel-bricks" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-navy-500 bg-white px-3 py-1 rounded-full border border-brand-navy-200">
                    Fundament & Beton
                  </span>
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-bold uppercase text-brand-navy-900 leading-snug group-hover:text-brand-orange-600 transition-colors">
                  Støbearbejde og fundamenter, et solidt udgangspunkt
                </h3>
                <div className="mt-4 space-y-3.5 text-sm sm:text-base text-brand-navy-600 leading-relaxed flex-grow">
                  <p>
                    Vi udfører støbearbejde i beton, herunder støbning af fundamenter til huse og andre bygninger. Som entreprenør kan vi stå for udgravning, klargøring, støbning og laserafretning, så arbejdsgangen hænger sammen fra den første udgravning og frem til betonen er på plads.
                  </p>
                  <p>
                    Vores entreprenørarbejde omfatter også belægningsarbejde og generelle bygge og anlægsopgaver for private og erhverv. Når flere faglige trin mødes i samme projekt, bidrager vores erfaring med kloak, jord, dræn og beton til et bedre overblik gennem hele forløbet.
                  </p>
                </div>
                <div className="mt-6 pt-5 border-t border-brand-navy-200/80 flex flex-wrap gap-2">
                  <Link to="/ydelser/stoebearbejde" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Støbearbejde &rarr;
                  </Link>
                  <Link to="/ydelser/laserafretning" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Laserafretning &rarr;
                  </Link>
                  <Link to="/ydelser/belaegning" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Belægning &rarr;
                  </Link>
                  <Link to="/ydelser/udgravning-til-byggeri" className="text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-white text-brand-navy-700 hover:text-brand-orange-600 border border-brand-navy-200 transition-colors">
                    Udgravning &rarr;
                  </Link>
                </div>
              </div>
            </ScrollReveal>

          </div>

          {/* 5. Local Presence Card: Din lokale autoriserede kloakmester omkring Sommersted */}
          <ScrollReveal direction="up" delay={250} className="mt-12">
            <div className="relative overflow-hidden rounded-2xl bg-brand-navy-900 p-8 sm:p-12 text-white shadow-xl border border-brand-navy-800">
              <div className="absolute right-0 top-0 -mt-8 -mr-8 w-72 h-72 rounded-full bg-brand-orange-500/10 blur-3xl pointer-events-none" />
              <div className="absolute left-1/3 bottom-0 -mb-10 w-80 h-80 rounded-full bg-brand-orange-500/5 blur-3xl pointer-events-none" />

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand-orange-400 bg-brand-orange-500/15 border border-brand-orange-500/30 px-3.5 py-1 rounded-full mb-4">
                    <i className="fa-solid fa-location-dot" />
                    <span>Lokalkendt autoriseret kloakmester</span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white leading-snug">
                    Din lokale autoriserede kloakmester omkring Sommersted
                  </h3>
                  <div className="mt-5 space-y-4 text-brand-navy-200 text-sm sm:text-base leading-relaxed max-w-3xl">
                    <p>
                      Vi har base i området omkring Sommersted og Jels og kører til opgaver i Vejen, Vojens, Haderslev, Kolding og Gram. Det lokale kendskab er en fordel, når kloakarbejde og entreprenøropgaver skal tilpasses grundens forhold, eksisterende anlæg og projektets rammer.
                    </p>
                    <p>
                      Uanset om du står med en stoppet kloak og har brug for akut kloakspuling, skal have etableret effektivt dræn eller planlægger ny belægning og støbearbejde, tager vi udgangspunkt i opgaven og forklarer mulighederne i et sprog, der er til at forstå. Kontakt Entreprenør Jan Nørgaard, og lad os tale om den løsning, der passer til dit projekt.
                    </p>
                  </div>

                  {/* Coverage area badges */}
                  <div className="mt-8 pt-6 border-t border-brand-navy-800/80">
                    <div className="text-xs font-bold uppercase tracking-wider text-brand-navy-400 mb-3">
                      Vi servicerer hele lokalområdet, herunder:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['Sommersted', 'Jels', 'Vejen', 'Vojens', 'Haderslev', 'Kolding', 'Gram'].map((city) => (
                        <span
                          key={city}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-navy-800/90 border border-brand-navy-700 text-xs font-bold uppercase tracking-wider text-white shadow-sm"
                        >
                          <i className="fa-solid fa-map-pin text-brand-orange-400 text-[10px]" />
                          {city}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Direct Action Column */}
                <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-4 justify-center">
                  <div className="rounded-xl bg-brand-navy-800/80 border border-brand-navy-700 p-6 sm:p-7 text-center backdrop-blur-sm shadow-inner">
                    <span className="block text-xs font-bold uppercase tracking-wider text-brand-orange-400 mb-1">
                      Klar til at hjælpe
                    </span>
                    <span className="block font-display text-xl font-bold uppercase text-white mb-2">
                      Få et uforpligtende tilbud
                    </span>
                    <p className="text-xs text-brand-navy-300 mb-5 leading-relaxed">
                      Fortæl os om dit projekt – vi vender hurtigt tilbage med kompetent rådgivning og en skarp pris.
                    </p>
                    <Link
                      to="/kontakt"
                      className="w-full inline-flex items-center justify-center gap-2 rounded bg-brand-orange-500 py-3 px-5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600 shadow-md"
                    >
                      Kontakt Jan Nørgaard <i className="fa-regular fa-arrow-right" />
                    </Link>
                    <a
                      href="tel:+4520483818"
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded border border-brand-navy-600 py-2.5 px-5 font-display text-xs font-bold uppercase tracking-wider text-brand-navy-200 transition-colors hover:bg-white hover:text-brand-navy-900"
                    >
                      <i className="fa-solid fa-phone text-brand-orange-400" /> Ring 20 48 38 18
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 4. SELECTED SERVICES */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up" className="text-center max-w-3xl mx-auto">
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-brand-navy-900 sm:text-4xl">
              Vores primære arbejdsområder
            </h2>
            <p className="mt-4 text-gray-600">
              Vi tilbyder et bredt udvalg af entreprenør- og kloakydelser. Her er et udvalg og vores mest efterspurgte kompetencer.
            </p>
          </ScrollReveal>

          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, expandedServices ? 12 : 3).map((service, idx) => (
              <ScrollReveal
                key={service.id}
                delay={idx * 100}
                direction="up"
                className="flex flex-col"
              >
                <div className="flex flex-col h-full rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:border-brand-orange-500/40">
                  <div className="text-brand-navy-900 mb-2">
                    <i className={`${service.icon} text-3xl`} />
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold text-brand-navy-900">
                    <Link to={`/ydelser/${service.slug.da}`} className="hover:text-brand-orange-600 transition-colors">
                      {service.title}
                    </Link>
                  </h3>
                  <p className="mt-2 flex-grow text-sm text-gray-600 leading-relaxed">
                    {service.intro}
                  </p>
                  <div className="mt-6">
                    <Link
                      to={`/ydelser/${service.slug.da}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange-600 hover:text-brand-orange-700"
                    >
                      Læs mere &rarr;
                    </Link>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <div className="mt-12 text-center flex flex-col sm:flex-row justify-center items-center gap-4">
            <button
              onClick={() => setExpandedServices(!expandedServices)}
              className="inline-flex items-center gap-2 rounded border border-brand-navy-900 px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-brand-navy-900 transition-colors hover:bg-brand-navy-900 hover:text-white cursor-pointer"
            >
              {expandedServices ? 'Vis færre ydelser' : 'Vis flere ydelser'} <i className={`fa-regular ${expandedServices ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </div>
        </div>
      </section>

      {/* 5. ABOUT THE COMPANY (Kort "Om virksomheden") */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-center">
            <ScrollReveal direction="left" className="order-2 lg:order-1">
              <figure>
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/stoebearbejde-fundament-og-kloak.jpg"
                  alt="Færdigstøbt betonfundament og sokkel med kloakrør og klargjort sandpude"
                  title="Støbearbejde, fundament og kloakføring"
                  className="rounded-lg shadow-xl border border-brand-navy-800/10 w-full"
                />
                <figcaption className="mt-2 text-xs text-brand-navy-500 italic">
                  Præcisionsstøbning af fundament og integreret kloakføring på byggeplads.
                </figcaption>
              </figure>
            </ScrollReveal>
            <ScrollReveal direction="right" className="order-1 lg:order-2">
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-brand-navy-900 sm:text-4xl">
                Vi bygger på tillid og godt håndværk
              </h2>
              <p className="mt-6 text-brand-navy-600 leading-relaxed">
                Hos Entreprenør Jan Nørgaard ApS tror vi på, at langvarige kunderelationer skabes gennem ærlighed, punktlighed og uovertruffen kvalitet. Vi rådgiver altid uvildigt og finder den mest økonomiske og holdbare løsning på dit projekt.
              </p>
              <p className="mt-4 text-brand-navy-600 leading-relaxed">
                Vores autoriserede kloakmester-titel er din garanti for, at alt arbejde under jorden udføres i nøje overensstemmelse med gældende miljø- og byggelovgivning – uanset om opgaven gælder effektiv kloakspuling, etablering af beskyttende dræn eller afsluttende, holdbar belægning.
              </p>
              <div className="mt-8">
                <Link
                  to="/om-os"
                  className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-wider text-brand-orange-600 hover:text-brand-orange-700"
                >
                  Læs mere om hvem vi er &rarr;
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* 6. CTA SECTION */}
      <section className="bg-brand-orange-500 text-white py-16">
        <ScrollReveal direction="up" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold uppercase tracking-tight sm:text-4xl text-white">
            Har du et projekt i tankerne?
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-lg font-medium text-white/90">
            Kontakt os i dag for en uforpligtende snak om dit projekt. Vi kommer gerne ud og besigtiger opgaven.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="tel:+4520483818"
              className="inline-flex items-center gap-2 rounded bg-brand-navy-900 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-navy-800"
            >
              <i className="fa-solid fa-phone" /> Ring 20 48 38 18
            </a>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 rounded border-2 border-white px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-brand-navy-900"
            >
              Skriv en besked
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* 7. REFERENCES/REVIEWS */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <ScrollReveal direction="up" className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-brand-navy-900 sm:text-4xl text-center md:text-left">
                Anbefalinger fra tilfredse kunder
              </h2>
            </div>
            <div className="flex justify-center gap-3 mt-6 md:mt-0">
              <button
                onClick={() => scroll('left')}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-navy-200 bg-white text-brand-navy-800 transition-colors hover:border-brand-orange-500 hover:bg-brand-orange-50 hover:text-brand-orange-600 cursor-pointer"
                aria-label="Forrige anmeldelse"
              >
                <i className="fa-regular fa-arrow-left text-lg" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-navy-200 bg-white text-brand-navy-800 transition-colors hover:border-brand-orange-500 hover:bg-brand-orange-50 hover:text-brand-orange-600 cursor-pointer"
                aria-label="Næste anmeldelse"
              >
                <i className="fa-regular fa-arrow-right text-lg" />
              </button>
            </div>
          </ScrollReveal>

          {/* Carousel container */}
          <ScrollReveal direction="up" delay={150}>
            <div
              ref={carouselRef}
              className="flex gap-6 overflow-x-auto scroll-smooth pb-4 scrollbar-none snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {reviews.map((review, idx) => (
                <div
                  key={idx}
                  className="w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] flex-shrink-0 snap-start rounded-lg bg-white p-6 shadow-md border border-brand-navy-100 flex flex-col justify-between transition-transform hover:-translate-y-1"
                >
                  <div>
                    <div className="flex gap-1 text-brand-orange-500 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="fa-solid fa-star text-sm" />
                      ))}
                    </div>
                    <p className="text-sm text-brand-navy-600 italic leading-relaxed">
                      "{review.text}"
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-brand-navy-100">
                    <span className="block text-sm font-bold text-brand-navy-900">
                      {review.name}
                    </span>
                    <span className="block text-xs text-brand-navy-500">
                      {review.company} {review.service && `· ${review.service}`}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* 8. CONTACT SECTION */}
      <section id="kontakt" className="py-20 bg-brand-navy-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {/* Contact Info */}
            <ScrollReveal direction="left">
              <h2 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-brand-navy-900 sm:text-4xl">
                Lad os tage en uforpligtende snak
              </h2>
              <p className="mt-6 text-brand-navy-600 leading-relaxed">
                Har du spørgsmål til vores ydelser, eller ønsker du et uforpligtende tilbud på en opgave? Udfyld formularen, så vender vi tilbage til dig hurtigst muligt – typisk inden for 24 timer.
              </p>

              <div className="mt-8 divide-y divide-gray-200 border-y border-gray-200">
                <div className="flex items-center gap-4 py-4">
                  <i className="fa-solid fa-phone text-brand-orange-500 text-xl w-6 shrink-0 text-center" />
                  <a
                    href="tel:+4520483818"
                    className="text-lg font-bold text-brand-navy-900 hover:text-brand-orange-600 transition-colors whitespace-nowrap"
                  >
                    20 48 38 18
                  </a>
                </div>

                <div className="flex items-center gap-4 py-4">
                  <i className="fa-solid fa-envelope text-brand-orange-500 text-xl w-6 shrink-0 text-center" />
                  <a
                    href="mailto:jan@jannoergaard.dk"
                    className="text-lg font-bold text-brand-navy-900 hover:text-brand-orange-600 transition-colors whitespace-nowrap"
                  >
                    jan@jannoergaard.dk
                  </a>
                </div>

                <div className="flex items-center gap-4 py-4">
                  <i className="fa-solid fa-location-dot text-brand-orange-500 text-xl w-6 shrink-0 text-center" />
                  <span className="text-lg font-bold text-brand-navy-900">
                    Ørstedvej 22, 6560 Sommersted
                  </span>
                </div>
              </div>
            </ScrollReveal>

            {/* Contact Form */}
            <ScrollReveal direction="right" className="rounded-lg bg-white p-8 shadow-lg border border-brand-navy-100">
              <h3 className="font-display text-xl font-bold uppercase text-brand-navy-900 mb-6">
                Send os en besked
              </h3>

              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Honeypot Field - Absolutely Hidden */}
                <div className="sr-only absolute" aria-hidden="true">
                  <label htmlFor="website_url">Hjemmeside URL</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                    Navn <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                    placeholder="Dit fulde navn"
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                      Telefonnummer <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                      placeholder="Telefonnummer"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                      E-mail <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                      placeholder="E-mail adresse"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                    Beskrivelse af opgaven <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows="4"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                    placeholder="Beskriv dit projekt eller problem så detaljeret som muligt..."
                  />
                </div>

                {formStatus.error && (
                  <div className="rounded bg-red-50 p-3 text-sm font-semibold text-red-600 border border-red-200">
                    <i className="fa-solid fa-circle-exclamation mr-1.5" /> {formStatus.error}
                  </div>
                )}

                {formStatus.success && (
                  <div className="rounded bg-green-50 p-3 text-sm font-semibold text-green-600 border border-green-200">
                    <i className="fa-solid fa-circle-check mr-1.5" /> Tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus.submitting}
                  className="w-full rounded bg-brand-orange-500 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600 disabled:opacity-50"
                >
                  {formStatus.submitting ? (
                    <span>Sender... <i className="fa-solid fa-spinner animate-spin ml-1" /></span>
                  ) : (
                    <span>Send besked <i className="fa-regular fa-paper-plane ml-1" /></span>
                  )}
                </button>
              </form>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
